import isEmpty from 'lodash/isEmpty';

import { MODULE_NAME } from './const';
import {
	SET_PROJECTS_LOADED,
	SET_PROJECT_DATA,
	SET_PROJECT_SHARED_DATA,
	SET_PROJECT_SHARED_DATA_LOADING,
	SET_PROJECT_PERMISSIONS,
	BULK_SET_PROJECTS_SHARED_DATA,
	BULK_SET_PROJECTS_DATA,
	BULK_SET_PROJECTS_PERMISSIONS,
} from './mutations';
import state from './state';
import StoreModuleService from '../StoreModuleService';
import { getProject, data } from '../projects/getters';
import { selectProjectId } from '../selectedProject/actions';
import api from '@/api';
import { getAccountMembers } from '@/store/modules/account/actions';
import { arrayToObject, isNullOrUndefined } from '@/utils/helpers';

const buildProjectActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const createProject = buildProjectActionObject('createProject');
export const updateProject = buildProjectActionObject('updateProject');
export const setProjectData = buildProjectActionObject('setProjectData');
export const getProjectData = buildProjectActionObject('getProjectData');
export const getAllProjects = buildProjectActionObject('getAllProjects');
export const fetchProjectData = buildProjectActionObject('fetchProjectData');
export const fetchAllProjectsData = buildProjectActionObject('fetchAllProjectsData');
export const addWorkflowIdToProject = buildProjectActionObject('addWorkflowIdToProject');
export const removeWorkflowIdFromProject = buildProjectActionObject('removeWorkflowIdFromProject');
export const collaborateNewProject = buildProjectActionObject('collaborateNewProject');
export const collaborateUsersNewProject = buildProjectActionObject('collaborateUsersNewProject');
export const collaborateGroupsNewProject = buildProjectActionObject('collaborateGroupsNewProject');
export const setProjectShareData = buildProjectActionObject('setProjectShareData');
export const updateProjectMemberRole = buildProjectActionObject('updateProjectMemberRole');
export const setProjectPermissions = buildProjectActionObject('setProjectPermissions');
export const fetchAllProjectPermissions = buildProjectActionObject('fetchAllProjectPermissions');
export const getAllProjectShareData = buildProjectActionObject('getAllProjectShareData');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getProjectData.action]({ dispatch }, payload) {
		const { id, forceGet } = payload;
		const dataIsMissing = isNullOrUndefined(state.data[id]);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			await dispatch(fetchProjectData.action, { id });
		}
	},
	[setProjectData.action]({ commit }, payload) {
		const { data: projectData } = payload;
		commit(SET_PROJECT_DATA, { key: projectData.id, value: projectData });
	},
	[addWorkflowIdToProject.action]({ dispatch, rootGetters }, payload) {
		const { projectId, workflowId } = payload;
		const project = rootGetters[getProject.fullPath](projectId);
		project.flows.push(workflowId);
		return dispatch(setProjectData.action, { data: project });
	},
	async [removeWorkflowIdFromProject.action]({ dispatch, rootGetters }, payload) {
		const { projectId, workflowId } = payload;
		const project = rootGetters[getProject.fullPath](projectId);
		project.flows = project.flows.filter((x) => x !== workflowId);
		await dispatch(setProjectData.action, { data: project });
	},
	async [getAllProjects.action]({ dispatch }, payload) {
		const { forceGet, includingWorkspaceAdminProjects } = payload;
		const dataIsMissing = isEmpty(state.data);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			await dispatch(fetchAllProjectsData.action, { includingWorkspaceAdminProjects });
		}
	},
	async [fetchProjectData.action]({ dispatch }, payload) {
		const { id } = payload;
		const ret = await api.getProject(id);
		await dispatch(setProjectData.action, { data: ret });
	},
	async [fetchAllProjectsData.action]({ dispatch, commit }, { includingWorkspaceAdminProjects }) {
		const projectsApi = includingWorkspaceAdminProjects ? api.getAllProjects : api.getProjects;
		try {
			const projects = await projectsApi();
			await dispatch(fetchAllProjectPermissions.action);
			commit(BULK_SET_PROJECTS_DATA, { data: arrayToObject(projects) });
			await dispatch(getAllProjectShareData.action, { projectIdsToGet: projects.map((x) => x.id) });
		} finally {
			commit(SET_PROJECTS_LOADED, true);
		}
	},
	async [createProject.action]({ dispatch }, payload) {
		const { project } = payload;
		const ret = await api.createProject(project);
		await dispatch(setProjectData.action, { data: ret });
		await dispatch(setProjectPermissions.action, { projectId: ret.id, force: true });
		await dispatch(selectProjectId.fullPath, { id: ret.id }, { root: true });
		return ret;
	},
	async [updateProject.action]({ dispatch }, payload) {
		const { project } = payload;
		const updatedProject = await api.updateProject(project);
		await dispatch(setProjectData.action, { data: updatedProject });
	},
	async [collaborateNewProject.action]({ dispatch }, payload) {
		const { users, groups } = payload;
		let response = { shared_with: [], invited_and_shared: [] };
		if (users?.length > 0) {
			const { users, ...body } = payload;
			const emails = users.map((x) => x.email);
			const ret = await dispatch(collaborateUsersNewProject.action, { ...body, emails });
			if (ret) {
				response = { ...response, ...ret };
			}
		}
		if (groups?.length > 0) {
			await dispatch(collaborateGroupsNewProject.action, payload);
		}
		await dispatch(setProjectShareData.action, { id: payload.projectId, force: true });
		dispatch(getAccountMembers.fullPath, { forceGet: true }, { root: true });
		return response;
	},
	// eslint-disable-next-line
	async [collaborateUsersNewProject.action]({}, payload) {
		const { projectId: id, emails, role, emailMessage, isSendMail, inviteUserRole } = payload;
		return api.collaborateNewProject(id, emails, role, emailMessage, isSendMail, inviteUserRole);
	},
	// eslint-disable-next-line
	async [collaborateGroupsNewProject.action]({}, payload) {
		const { projectId: id, groups, role, emailMessage, isSendMail } = payload;
		return api.collaborateGroupsNewProject(id, groups, role, emailMessage, isSendMail);
	},
	async [setProjectShareData.action]({ commit, state }, payload) {
		const { id: projectId, force } = payload;
		const needUpdate = !state.sharedData[projectId];
		if (needUpdate || force) {
			commit(SET_PROJECT_SHARED_DATA_LOADING, { isLoading: true });
			const ret = await api.getProjectSharedData(projectId);
			commit(SET_PROJECT_SHARED_DATA, { key: projectId, value: ret });
			commit(SET_PROJECT_SHARED_DATA_LOADING, { isLoading: false });
		}
	},
	async [getAllProjectShareData.action]({ commit, rootGetters }, { projectIdsToGet }) {
		const allProjects = rootGetters[data.fullPath];
		const projectsIds = projectIdsToGet ? projectIdsToGet : Object.keys(allProjects);
		commit(SET_PROJECT_SHARED_DATA_LOADING, { isLoading: true });
		const allProjectShareData = await api.getAllProjectShareData(projectsIds);
		commit(SET_PROJECT_SHARED_DATA_LOADING, { isLoading: false });
		commit(BULK_SET_PROJECTS_SHARED_DATA, { data: allProjectShareData });
	},

	async [updateProjectMemberRole.action]({ dispatch }, { projectId, emails, groups, role }) {
		if (groups || emails) {
			await api.updateProjectRoles(projectId, role, groups, emails);
			dispatch(setProjectShareData.action, { id: projectId, force: true });
		}
	},
	async [setProjectPermissions.action]({ commit }, { projectId, force }) {
		const needUpdate = !state.permissions[projectId];
		if (needUpdate || force) {
			const response = await api.getProjectPermissions(projectId);
			commit(SET_PROJECT_PERMISSIONS, { key: projectId, value: response.permissions });
		}
	},
	async [fetchAllProjectPermissions.action]({ commit }) {
		const response = await api.getAllProjectPermissions();
		commit(BULK_SET_PROJECTS_PERMISSIONS, { data: response.permissions });
	},
};

export default actions;
